import { useEffect, useRef, useState } from 'react';
import { formatTimer } from '../hooks/useCountdown';

type Props = {
  secondsLeft: number;
  size?: number;
  stroke?: number;
  scaleSec?: number;
  flashTrigger?: number;
  showLabel?: boolean;
  label?: string;
};

type State = 'idle' | 'warn' | 'danger';

function pickState(s: number): State {
  if (s <= 10) return 'danger';
  if (s <= 30) return 'warn';
  return 'idle';
}

const STATE_COLOR: Record<State, string> = {
  idle: '#00e5ff',
  warn: '#ffd93d',
  danger: '#ff4d4d',
};

export function RingTimer({
  secondsLeft,
  size = 120,
  stroke = 6,
  scaleSec = 60,
  flashTrigger = 0,
  showLabel = true,
  label = 'TIMER',
}: Props) {
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const clamped = Math.min(secondsLeft, scaleSec);
  const progress = scaleSec > 0 ? clamped / scaleSec : 0;
  const dashOffset = c * (1 - progress);

  const state = pickState(secondsLeft);
  const color = STATE_COLOR[state];

  const wrapRef = useRef<HTMLDivElement>(null);
  const [flashKey, setFlashKey] = useState(0);

  useEffect(() => {
    if (flashTrigger > 0) setFlashKey((k) => k + 1);
  }, [flashTrigger]);

  const innerSafe = Math.max(0, (radius - stroke - 4) * 2);
  const maxByWidth = innerSafe / (5 * 0.58);
  const fontSize = Math.max(9, Math.min(size * 0.26, maxByWidth));
  const labelSize = Math.max(7, fontSize * 0.34);

  return (
    <div
      ref={wrapRef}
      className={`relative inline-flex items-center justify-center ${state === 'danger' ? 'anim-pulse-danger' : ''}`}
      style={{ width: size, height: size }}
    >
      <svg
        key={flashKey}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="anim-flash"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <defs>
          <filter id={`glow-${size}-${state}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#1c2330"
          strokeWidth={stroke}
          fill="none"
        />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius - stroke - 2}
          stroke="#11161f"
          strokeWidth="1"
          fill="rgba(5, 8, 16, 0.55)"
        />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          filter={`url(#glow-${size}-${state})`}
          style={{
            transition: 'stroke-dashoffset 0.5s linear, stroke 0.3s ease',
          }}
        />

        {[0, 90, 180, 270].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const cx = size / 2 + (radius + stroke / 2 + 4) * Math.cos(rad);
          const cy = size / 2 + (radius + stroke / 2 + 4) * Math.sin(rad);
          return (
            <circle key={deg} cx={cx} cy={cy} r={1.2} fill={color} fillOpacity="0.55" />
          );
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span
          className="font-mono font-semibold tabular-nums tracking-tight"
          style={{
            fontSize,
            color,
            textShadow: `0 0 ${size * 0.06}px ${color}88`,
            lineHeight: 1,
          }}
        >
          {formatTimer(secondsLeft)}
        </span>
        {showLabel && (
          <span
            className="font-mono uppercase tracking-[0.25em] mt-1"
            style={{ fontSize: labelSize, color: '#7a8595' }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}
